import { Component, NgZone, DOCUMENT } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { trigger, transition, style, animate } from '@angular/animations';

import { Inject } from '@angular/core';
import { LanguageService } from './services/language.service';
import { ThemeService } from './services/theme.service';
import { ProductSheetService } from './services/product-sheet.service';
import { AttractionDetailsSheetService } from './services/attraction-details-sheet.service';
import { LanguageSheetService } from './services/language-sheet.service';
import { ReelService } from './services/reel.service';
import { UxCamService } from './services/uxcam.service';
import { AnalyticsService } from './services/analytics.service';
import { readTouristicOpen, stripTouristicOpen } from './services/touristic-notification-analytics';

@Component({
    selector: 'app-root',
    template: `
    <main [@routeAnimations]="prepareRoute(outlet)">
      <router-outlet #outlet="outlet"></router-outlet>
    </main>
    <!-- Global product-details bottom sheet, overlaid on top of the live screen. -->
    <app-product-details
      *ngIf="(sheet.changes$ | async)?.isOpen"
      [overlayMode]="true"
      [productId]="sheet.productId">
    </app-product-details>
    <!-- Global attraction-details bottom sheet (opened from the /attractions list). -->
    <app-attraction-details
      *ngIf="(attrSheet.changes$ | async)?.isOpen"
      [code]="attrSheet.code">
    </app-attraction-details>
    <!-- Global language-selection bottom sheet (first-visit prompt + header flag chip). -->
    <app-language-sheet
      *ngIf="(langSheet.changes$ | async)?.isOpen"
      [mode]="langSheet.mode">
    </app-language-sheet>
    <!-- Global full-screen Feather Reel media-banner player. -->
    <app-reel-overlay
      *ngIf="(reel.changes$ | async)?.isOpen"
      [banner]="reel.banner!">
    </app-reel-overlay>
  `,
    animations: [
        trigger('routeAnimations', [
            transition('* <=> *', [
                style({ position: 'relative' }),
                style({ opacity: 0 }),
                animate('300ms ease-out', style({ opacity: 1 }))
            ])
        ])
    ],
    standalone: false
})
export class AppComponent {
  title = 'feather-menu';

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private languageService: LanguageService,
    private themeService: ThemeService,
    public sheet: ProductSheetService,
    public attrSheet: AttractionDetailsSheetService,
    public langSheet: LanguageSheetService,
    public reel: ReelService,
    private uxcam: UxCamService,
    private analytics: AnalyticsService,
    @Inject(DOCUMENT) private document: Document
  ) { }

  ngOnInit(): void {
    // Installs the recorder's in-memory queue now; the SDK itself downloads in an idle slice
    // after `load`, so it never competes with splash's boot requests.
    this.uxcam.init();

    // Re-apply the venue theme on every bootstrap (covers refresh / direct-nav
    // where splash never ran but clientData is already in sessionStorage).
    this.themeService.applyThemeFromStorage();

    this.languageService.currentLang$.subscribe(lang => {
      this.document.documentElement.lang = lang;
    });

    // The COLD half of a notification tap: the service worker had no tab to talk to and called
    // `clients.openWindow`, so this bootstrap IS the open. The warm half is handled in the
    // PUSH_NAVIGATE listener below — two call sites because only one of the two SW branches can
    // reach a running page, and the cold one is the common case for a notification that arrives
    // hours after the scan.
    this.reportTouristicOpen(window.location.search);

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        const msg = event.data;
        if (!msg || msg.type !== 'PUSH_NAVIGATE') return;

        // The WARM half: a tab was already open, so no navigation carried the markers into the
        // address bar. Read them off the message instead. Nothing to strip — the URL never held
        // them — and the SW posts once per tap, so this cannot double-count.
        this.reportTouristicOpen(this.searchOf(msg.url));

        this.ngZone.run(() => {
          let target: string | null = null;
          if (msg.path) {
            target = '/' + msg.path;
          } else if (msg.url) {
            try {
              const parsed = new URL(msg.url, window.location.origin);
              const redirect = parsed.searchParams.get('redirect');
              if (redirect) target = '/' + redirect;
            } catch (e) {}
          }
          if (!target) return;

          // Same-URL target: the router ignores the navigation (onSameUrlNavigation
          // 'ignore'), so replaceUrl can't overwrite the sheet's synthetic history
          // entry. Just close the sheet — close() pops that entry itself — and skip
          // the no-op navigation.
          if (target === this.router.url) {
            if (this.sheet.isOpen) this.sheet.close();
            return;
          }

          // Different-URL target: close the sheet (if open) before navigating, so
          // the overlay + body scroll-lock don't persist over the destination.
          // replaceUrl overwrites the sheet's synthetic history entry so it isn't
          // orphaned behind the new route.
          const replaceUrl = this.sheet.closeForNavigation();
          this.router.navigateByUrl(target, { replaceUrl });
        });
      });
    }
  }

  /**
   * Report a touristic notification tap, once.
   *
   * The markers are stripped from the address bar immediately afterwards, which is what stops a
   * refresh — or a guest sharing the link — logging a second open for one tap. `replaceState`
   * rather than a navigation: the route is already correct, and pushing would leave the marked URL
   * one Back press away.
   *
   * Non-touristic navigations cost one `URLSearchParams` lookup and log nothing.
   */
  private reportTouristicOpen(search: string): void {
    const open = readTouristicOpen(search);
    if (!open) return;

    this.analytics.log('touristic_notification_open', open);

    const cleaned = stripTouristicOpen(window.location.href);
    if (cleaned) {
      window.history.replaceState(window.history.state, '', cleaned);
    }
  }

  /** The query string of a URL that may be relative, or '' when it cannot be parsed. */
  private searchOf(url: string | null | undefined): string {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).search;
    } catch (e) {
      return '';
    }
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet && outlet.activatedRouteData && outlet.activatedRouteData['animation'];
  }
}
